import { useState, useEffect } from "react";
import { listEshopSites, createEshopSite, eshopSiteAction } from "../lib/api";
import type { EshopSiteView } from "../lib/api";

/**
 * e-Shop 站點管理（Go-Live M9，2026-08-26）
 *
 * 每個 e-Shop 客戶站一行：site token 建立／輪換時明文只顯示一次；
 * 開通三步（wizard 掃碼→複製客戶站 webhook URL→RPMSMS 後台貼上）見
 * eshop-template DEPLOY-CHECKLIST（aibot 模式段）。
 */
export default function EshopSitesPage() {
  const [sites, setSites] = useState<EshopSiteView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [slug, setSlug] = useState("");
  const [phone, setPhone] = useState("");
  const [rateLimit, setRateLimit] = useState(60);

  const [tokenReveal, setTokenReveal] = useState<{ slug: string; token: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    loadSites();
  }, []);

  async function loadSites() {
    setLoading(true);
    setError("");
    try {
      setSites(await listEshopSites());
    } catch {
      setError("網路錯誤（connector 未部署或 ADMIN_API_KEY 未設定？）");
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    setError("");
    setNotice("");
    if (!slug || !phone) {
      setError("site_slug 與裝置號碼為必填");
      return;
    }
    try {
      const data = await createEshopSite(slug, phone, rateLimit);
      if (data.token && data.site) {
        setTokenReveal({ slug: data.site.site_slug, token: data.token });
        setSlug("");
        setPhone("");
        setRateLimit(60);
        await loadSites();
      } else {
        setError(data.error || "建立失敗");
      }
    } catch {
      setError("網路錯誤");
    }
  }

  async function handleAction(site: EshopSiteView, action: string, extra: Record<string, unknown> = {}) {
    setBusyId(site.id);
    setError("");
    setNotice("");
    try {
      const data = await eshopSiteAction(site.id, action, extra);
      if (action === "rotate" && data.token) {
        setTokenReveal({ slug: site.site_slug, token: data.token });
      } else if (action === "canary") {
        setNotice(`canary（${site.site_slug}）：${data.success ? "✅ 真實發送成功" : `❌ ${data.detail || data.error}`}`);
      } else if (data.error) {
        setError(data.error);
      }
      await loadSites();
    } catch {
      setError("網路錯誤");
    } finally {
      setBusyId(null);
    }
  }

  function copyToken(token: string) {
    navigator.clipboard.writeText(token).catch(() => {
      /* 剪貼簿不可用時手動選取即可 */
    });
  }

  return (
    <div>
      <div className="mb-6 p-4 bg-amber-950/20 border border-amber-700/30 rounded-xl text-xs text-amber-200/90">
        📌 <span className="font-semibold">功能定位（2026-10 收納）</span>：此頁為早期（2026-08）純出站閘道——新出站需求已由
        <span className="text-amber-300"> Channel Broker（/api/channel/*）</span>承接，新站點請經戰略夥伴流程開通；既有站點繼續服務至自然退役，本頁保留作存量管理。
      </div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-100">🛒 e-Shop 站點</h1>
        <button
          onClick={loadSites}
          className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors"
        >
          🔄 重新載入
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-lg text-sm">{error}</div>}
      {notice && <div className="mb-4 p-3 bg-sky-500/10 border border-sky-500/30 text-sky-300 rounded-lg text-sm whitespace-pre-wrap">{notice}</div>}

      {tokenReveal && (
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/40 rounded-lg">
          <p className="text-amber-300 font-bold mb-2">🔑 {tokenReveal.slug} 的 site token（只顯示這一次）</p>
          <div className="flex items-center gap-3">
            <code className="flex-1 p-2 bg-slate-950 border border-slate-700 rounded font-mono text-xs text-amber-200 break-all">
              {tokenReveal.token}
            </code>
            <button onClick={() => copyToken(tokenReveal.token)} className="px-3 py-2 text-xs bg-amber-600 hover:bg-amber-500 text-white rounded">
              複製
            </button>
            <button onClick={() => setTokenReveal(null)} className="px-3 py-2 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded">
              關閉
            </button>
          </div>
          <p className="text-xs text-amber-400/80 mt-2">存入客戶站 Pages secret：AIBOT_API_TOKEN（設定後必須 redeploy）</p>
        </div>
      )}

      {/* 建立表單 */}
      <div className="mb-8 p-4 bg-slate-900 border border-slate-800 rounded-lg">
        <h2 className="text-sm font-bold text-slate-300 mb-3">新增站點（＝一個客戶站＋一個專屬裝置號碼）</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-400">site_slug（客戶站 projectSlug，kebab-case）</span>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="demo-spice-shop"
              className="px-3 py-2 bg-slate-950 border border-slate-700 rounded text-sm text-slate-100 w-56"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-400">裝置號碼（該站專屬 WhatsApp，純數字）</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="8526xxxxxxx"
              className="px-3 py-2 bg-slate-950 border border-slate-700 rounded text-sm text-slate-100 w-44"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-400">限流／分鐘</span>
            <input
              type="number"
              min={1}
              max={600}
              value={rateLimit}
              onChange={(e) => setRateLimit(parseInt(e.target.value, 10) || 60)}
              className="px-3 py-2 bg-slate-950 border border-slate-700 rounded text-sm text-slate-100 w-24"
            />
          </label>
          <button onClick={handleCreate} className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg">
            ➕ 建立（生成 token）
          </button>
        </div>
      </div>

      {/* 站點列表 */}
      {loading ? (
        <p className="text-slate-400">載入中…</p>
      ) : sites.length === 0 ? (
        <p className="text-slate-500">尚無站點。</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b border-slate-800">
                <th className="py-2 pr-4">#</th>
                <th className="py-2 pr-4">site_slug</th>
                <th className="py-2 pr-4">裝置號碼</th>
                <th className="py-2 pr-4">狀態</th>
                <th className="py-2 pr-4">限流</th>
                <th className="py-2 pr-4">24h 發送</th>
                <th className="py-2 pr-4">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {sites.map((s) => (
                <tr key={s.id} className="text-slate-200">
                  <td className="py-3 pr-4 text-slate-500">{s.id}</td>
                  <td className="py-3 pr-4 font-mono text-sky-300">{s.site_slug}</td>
                  <td className="py-3 pr-4 font-mono">{s.device_phone}</td>
                  <td className="py-3 pr-4">
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        s.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-600/30 text-slate-400"
                      }`}
                    >
                      {s.status === "active" ? "active" : "disabled"}
                    </span>
                  </td>
                  <td className="py-3 pr-4">{s.rate_limit_per_min}/min</td>
                  <td className="py-3 pr-4">
                    <span className={s.failed24h > 0 ? "text-amber-300" : "text-slate-300"}>
                      {s.send24h}（失敗 {s.failed24h}）
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex flex-wrap gap-2">
                      <button
                        disabled={busyId === s.id}
                        onClick={() => handleAction(s, s.status === "active" ? "disable" : "enable")}
                        className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-50"
                      >
                        {s.status === "active" ? "停用" : "啟用"}
                      </button>
                      <button
                        disabled={busyId === s.id}
                        onClick={() => {
                          const v = prompt(`${s.site_slug} 新限流（1-600）/分鐘：`, String(s.rate_limit_per_min));
                          if (v) handleAction(s, "rate-limit", { rate_limit_per_min: parseInt(v, 10) });
                        }}
                        className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-50"
                      >
                        調限流
                      </button>
                      <button
                        disabled={busyId === s.id}
                        onClick={() => {
                          if (confirm(`輪換 ${s.site_slug} 的 token？舊 token 即時失效，客戶站需更新 AIBOT_API_TOKEN 並 redeploy。`))
                            handleAction(s, "rotate");
                        }}
                        className="px-2 py-1 text-xs bg-amber-700/70 hover:bg-amber-600 rounded disabled:opacity-50"
                      >
                        輪換 token
                      </button>
                      <button
                        disabled={busyId === s.id}
                        onClick={() => handleAction(s, "canary")}
                        className="px-2 py-1 text-xs bg-emerald-700/70 hover:bg-emerald-600 rounded disabled:opacity-50"
                      >
                        🐤 canary 真發
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-8 p-4 bg-slate-900/60 border border-slate-800 rounded-lg text-xs text-slate-400 leading-relaxed">
        <p className="font-bold text-slate-300 mb-1">開通提醒（混合模式：出站經 AIBot、入站直達客戶站）</p>
        <p>① wizard（/bind?t=&amp;w=）用裝置號碼手機本體掃碼綁定；② 客戶站 DEPLOY-CHECKLIST 有其 webhook URL 一鍵複製；③ WTS Webhook 後台該裝置行手動貼上（供應商無 API＝永久人工）。</p>
        <p className="mt-1">canary 鐵律：真實發送才算數（「already connected」回應＝假陽性）。</p>
      </div>
    </div>
  );
}
